import {
  getScheduleCoachGroup,
  useDeleteScheduleCoachGroup
} from '#api/schedules';
import { Button, Text, Textarea } from '#atoms';
import { PATH } from '#const/path';
import queryKeys from '#hooks/queries/queryKeys';
import { useDeleteScheduleCoachMutation } from '#hooks/queries/schedules/useDeleteScheduleCoachMutation';
import { useScheduleCoachDetailQuery } from '#hooks/queries/schedules/useScheduleCoachDetailQuery';
import { useCoachMark } from '#hooks/useCoachMark';
import { Modal } from '#molecules';
import { useScheduleManage } from '#providers/SchedulesManage';
import { useToast } from '#providers/ToastProvider';
import CoachMark from '#shared/CoachMark';
import { PrevHeaderBarThreeDotTemplate } from '#templates';
import { dateDayKoFormatter } from '#utils/dateFormatter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { LeftTextCheckBox } from 'src/components/atoms/LeftCheckBox';
import Loading from 'src/components/molecules/Loading';
import useSetCoachIdByPathState from 'src/features/Calendar/hooks/useSetCoachIdByPathState';
import { ButtonGroup } from './ButtonGroup';
import GroupMember from './GroupMember';
import LectureTime from './LectureTime';
import Member from './Member';
import PrevNextLecture from './PrevNextLecture';
import Round from './Round';
import * as S from './styles';

interface DropdownMenuProps {
  onClickModify: () => void;
  onClickDelete: () => void;
}

export function WeeklyCalenderDetail() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [textAreaValue, setTextAreaValue] = useState<string>('');
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [cancelToday, setCancelToday] = useState(false);
  const navigate = useNavigate();
  const { lectureId: registeredLectureId } = useParams();
  const { mutate: deleteSchedule } = useDeleteScheduleCoachMutation();
  useSetCoachIdByPathState();

  const { data, isLoading } = useScheduleCoachDetailQuery({
    id: registeredLectureId
  });
  const { clearSelectedLecture } = useScheduleManage();

  // 그룹 상세 조회 api
  // const { data: groupCoachSchedule } = useGetScheduleCoachGroup(
  //   { lectureId: data?.lectureId || '', taughtAt: data?.taughtAt || '' },
  //   {
  //     enabled: data?.isGroup === true
  //   }
  // );

  const enabled = data ? data.isGroup : false;

  const { data: groupCoachSchedule } = useQuery(
    [`/schedules/coach/group/${data?.lectureId || ''}`, data?.taughtAt || ''],
    () => {
      if (!enabled) return null;
      return getScheduleCoachGroup({
        lectureId: data?.lectureId || '',
        taughtAt: data?.taughtAt || ''
      });
    }
  );

  const todayDate = data && dateDayKoFormatter(data.taughtAt);
  const startTime = dayjs(data?.taughtAt).format('HH:mm');
  const endTime = dayjs(data?.endedAt).format('HH:mm');

  const onSuccess = () => {
    showToast({
      type: 'success',
      message: '일정이 삭제되었습니다.'
    });
    queryClient.invalidateQueries([queryKeys.SCHEDULE]);
    navigate(-1);
  };

  const onError = () => {
    showToast({
      type: 'fail',
      message: '일정 삭제에 실패했습니다.'
    });
  };

  const { deleteScheduleCoachGroup } = useDeleteScheduleCoachGroup({
    lectureId: data?.lectureId || '',
    taughtAt: data?.taughtAt || '',
    onSuccess,
    onError
  });

  const handleDeleteSchedule = async () => {
    setIsOpenDeleteModal(false);
    deleteSchedule(
      {
        id: registeredLectureId as string,
        status: cancelToday ? 'CANCELLED' : 'WITHDRAWAL'
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const handleDeleteGroupSchedule = () => {
    setIsOpenDeleteModal(false);

    deleteScheduleCoachGroup();
  };

  const handleModify = () => {
    if (data?.isGroup) {
      navigate(
        `${
          PATH.Weekly_Calender_Schedule_Registration
        }?lecture-schedule-id=${encodeURIComponent(
          registeredLectureId as string
        )}&pre-tauthAt=${encodeURIComponent(data?.taughtAt as string)}`
      );

      return;
    }

    navigate(
      `${
        PATH.Weekly_Calender_Schedule_Registration
      }?lecture-schedule-id=${encodeURIComponent(
        registeredLectureId as string
      )}`
    );
  };

  useEffect(() => {
    return () => clearSelectedLecture();
  }, []);

  const { isCoachMark } = useCoachMark();

  if (isLoading) {
    return <Loading />;
  }

  if (!registeredLectureId || !data) {
    return <div style={{ color: '#fff' }}>데이터가 없습니다</div>;
  }

  return (
    <>
      <CoachMark />
      <PrevHeaderBarThreeDotTemplate
        defaultBackground
        disableDotButton={data.status === 'CANCELLED'}
        dotComponent={
          <DropdownMenu
            onClickModify={handleModify}
            onClickDelete={() => {
              setIsOpenDeleteModal(true);
            }}
          />
        }
      >
        <S.Container>
          <CoachMark.SpotLight
            style={{
              padding: '20px',
              marginBottom: isCoachMark ? '0' : '40px'
            }}
          >
            <CoachMark.Tooltip
              position="top"
              message={
                '해당 수업의 시간과' +
                '\n' +
                '직전/다음 수업까지 한 번에 알려줘요'
              }
            />
            <LectureTime
              todayDate={todayDate}
              startTime={startTime}
              endTime={endTime}
            />
            <PrevNextLecture
              round={data.round}
              totalRound={data.totalRound}
              personId={data.personId}
              confirmationFormId={data.confirmationFormId}
            />
          </CoachMark.SpotLight>

          {data.isGroup ? (
            <>
              {groupCoachSchedule?.map(item => {
                return <GroupMember key={item.id} item={item} data={data} />;
              })}
            </>
          ) : (
            <div>
              <Member data={data} />
              <S.LabeledInfo>
                <S.LabeledInfoTitle>수업</S.LabeledInfoTitle>
                <S.LabeledInfoDescription>
                  {data?.title}
                </S.LabeledInfoDescription>
              </S.LabeledInfo>

              <Round data={data} />
            </div>
          )}
          <S.TextAreaBox>
            <S.LabeledInfoTitle>메모</S.LabeledInfoTitle>
            <Textarea
              value={textAreaValue}
              placeholder="회원과 관련된 메모를 적어주세요!"
              onChange={e => setTextAreaValue(e)}
              height={20}
              limit={500}
            />
          </S.TextAreaBox>

          <CoachMark.SpotLight
            style={{
              width: '100%',
              position: 'fixed',
              bottom: '0px',
              left: '50%',
              transform: 'translate3d(-50%, 0px, 0px)'
            }}
          >
            <ButtonGroup data={data} />
          </CoachMark.SpotLight>
          <CoachMark.SpotLight
            style={{
              width: 'calc(100% - 20px)',
              height: '77px',
              position: 'fixed',
              bottom: '7px',
              left: '50%',
              zIndex: 100,
              transform: 'translate3d(-50%, 0px, 0px)',
              maxWidth: 'var(--max-width)'
            }}
          >
            <CoachMark.Tooltip
              position="top"
              message={
                '바로 운동차트를 작성하거나' +
                '\n' +
                '회원과 채팅을 시작할 수 있어요'
              }
            />
          </CoachMark.SpotLight>
        </S.Container>

        {isOpenDeleteModal && (
          <Modal onClose={() => setIsOpenDeleteModal(false)}>
            <div>
              <Text style={{ textAlign: 'center' }}>
                선택하신 일정이 삭제돼요. <br /> 그래도 계속하시겠아요?
              </Text>
            </div>
            {!data.isGroup && (
              <LeftTextCheckBox
                id={'sameDayCancellation'}
                checked={cancelToday}
                onClick={() => setCancelToday(prev => !prev)}
                label="수업 횟수 차감"
                labelLocation="right"
              />
            )}

            <S.ModalButtonContainer>
              <Button onClick={() => setIsOpenDeleteModal(false)}>취소</Button>
              <Button
                onClick={
                  data.isGroup
                    ? handleDeleteGroupSchedule
                    : handleDeleteSchedule
                }
                background="anti"
              >
                확인
              </Button>
            </S.ModalButtonContainer>
          </Modal>
        )}
      </PrevHeaderBarThreeDotTemplate>
    </>
  );
}

function DropdownMenu({ onClickModify, onClickDelete }: DropdownMenuProps) {
  return (
    <S.DropdownWrapper>
      <S.DropdownButton onClick={onClickModify}>수정</S.DropdownButton>
      <S.DropdownButtonDelete onClick={onClickDelete}>
        삭제
      </S.DropdownButtonDelete>
    </S.DropdownWrapper>
  );
}
